import React from 'react';
import { ExternalLink } from 'lucide-react';

export interface RenderOptions {
  onInternalLinkClick?: (path: string) => void;
}

// Rules for automated SEO contextual linking (2-3 internal hubs, 1-2 authoritative external references)
export const AUTO_LINK_RULES: { 
  match: RegExp; 
  href: string; 
  isExternal: boolean; 
  title: string; 
}[] = [
  // 1. High-Authority External Reference Links (Safe E-E-A-T Bank)
  { 
    match: /\bStripe Connect\b/i, 
    href: 'https://stripe.com/docs/connect', 
    isExternal: true, 
    title: 'Official Stripe Connect Escrow Documentation' 
  },
  { 
    match: /\bPayoneer\b/i, 
    href: 'https://www.payoneer.com/', 
    isExternal: true, 
    title: 'Payoneer Cross-Border Payments Infrastructure' 
  },
  { 
    match: /\bcommercial escrow principles\b|\bWikipedia Escrow\b/i, 
    href: 'https://en.wikipedia.org/wiki/Escrow', 
    isExternal: true, 
    title: 'Wikipedia Financial Escrow Mechanism & Legal Definition' 
  },
  { 
    match: /\bNASSCOM\b/i, 
    href: 'https://www.nasscom.in/', 
    isExternal: true, 
    title: 'NASSCOM Global Engineering & Tech Talent Council' 
  },
  { 
    match: /\bStatista\b/i, 
    href: 'https://www.statista.com/', 
    isExternal: true, 
    title: 'Statista Global Workforce & Tech Market Research' 
  },
  { 
    match: /\bUpwork Research Institute\b/i, 
    href: 'https://www.upwork.com/research/', 
    isExternal: true, 
    title: 'Upwork Research Institute Labor Market Reports' 
  },
  { 
    match: /\bGoogle Developers\b/i, 
    href: 'https://developers.google.com/', 
    isExternal: true, 
    title: 'Google Developers Official Standards & APIs' 
  },
  { 
    match: /\bGitHub\b/i, 
    href: 'https://github.com/', 
    isExternal: true, 
    title: 'GitHub Developer Platform & Source Code Repositories' 
  },
  { 
    match: /\bIRS Form W-9\b/i, 
    href: 'https://www.irs.gov/forms-pubs/about-form-w-9', 
    isExternal: true, 
    title: 'Internal Revenue Service W-9 Contractor Guidelines' 
  },
  { 
    match: /\bW-8BEN\b/i, 
    href: 'https://www.irs.gov/forms-pubs/about-form-w-8-ben', 
    isExternal: true, 
    title: 'IRS Certificate of Foreign Status of Beneficial Owner' 
  },
  { 
    match: /\bUK IR35\b/i, 
    href: 'https://www.gov.uk/guidance/understanding-off-payroll-working-ir35', 
    isExternal: true, 
    title: 'UK Government Off-Payroll Working IR35 Legislation' 
  },
  { 
    match: /\bEuropean GDPR\b/i, 
    href: 'https://gdpr.eu', 
    isExternal: true, 
    title: 'European Union General Data Protection Regulation Portal' 
  },

  // 2. Contextual Internal HarryDesk Hub Links
  { 
    match: /\b(remote developer jobs|remote software jobs|freelance developer jobs|remote tech jobs|remote jobs board)\b/i, 
    href: '/jobs', 
    isExternal: false, 
    title: 'Browse Verified Zero-Commission Remote Software Jobs on HarryDesk' 
  },
  { 
    match: /\b(zero-commission pricing|escrow fees|0% platform commission|marketplace pricing)\b/i, 
    href: '/pricing', 
    isExternal: false, 
    title: 'Review HarryDesk Zero-Commission Pricing and Milestone Escrow Fees' 
  },
  { 
    match: /\b(milestone escrow protection|how escrow works|milestone contract protection|safe escrow payments)\b/i, 
    href: '/how-it-works', 
    isExternal: false, 
    title: 'Learn How HarryDesk Milestone Escrow Protects Clients and Freelancers' 
  },
  { 
    match: /\b(affiliate program|referral commission|ambassador network)\b/i, 
    href: '/affiliate-portal', 
    isExternal: false, 
    title: 'Join the HarryDesk Ambassador & Affiliate Referral Program' 
  }
];

/**
 * Parses inline markdown including:
 * - Markdown links [text](url)
 * - Markdown images ![alt](url)
 * - Bold **text**
 * - Italic *text*
 * - Inline code `code`
 * - Raw URLs
 * - Automated contextual internal/external links (max 1 link per matched rule)
 */
export function parseInlineMarkdown(
  text: string, 
  options?: RenderOptions, 
  usedRules: Set<string> = new Set()
): React.ReactNode[] {
  if (!text) return [];

  const tokenRegex = /(!\[([^\]]*)\]\(([^)]+)\))|(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(https?:\/\/[^\s<)"]+)/g;

  const elements: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyCounter = 0;

  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const plainText = text.substring(lastIndex, match.index);
      elements.push(...enrichPlainTextWithAutoLinks(plainText, keyCounter++, options, usedRules));
    }

    if (match[1]) {
      // Image: ![alt](url)
      const alt = match[2] || 'HarryDesk Guide Visual Asset';
      const src = match[3];
      elements.push(
        <figure key={`img-${keyCounter++}`} className="my-6 rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50">
          <img
            src={src}
            alt={alt}
            width={1200}
            height={630}
            className="w-full aspect-[16/9] object-cover"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
          />
          {alt && (
            <figcaption className="text-xs text-center text-slate-500 py-2 px-4 font-mono bg-slate-100/70 border-t border-slate-200">
              {alt}
            </figcaption>
          )}
        </figure>
      );
    } else if (match[4]) {
      // Markdown link: [text](url)
      const linkText = match[5];
      const linkHref = match[6];
      elements.push(renderAnchorLink(linkHref, linkText, `link-${keyCounter++}`, options));
    } else if (match[7]) {
      // Bold: **text**
      elements.push(
        <strong key={`bold-${keyCounter++}`} className="font-bold text-slate-900">
          {match[8]}
        </strong>
      );
    } else if (match[9]) {
      // Italic: *text*
      elements.push(
        <em key={`italic-${keyCounter++}`} className="italic text-slate-800">
          {match[10]}
        </em>
      );
    } else if (match[11]) {
      // Inline code: `code`
      elements.push(
        <code key={`code-${keyCounter++}`} className="bg-slate-100 text-amber-800 px-1.5 py-0.5 rounded font-mono text-xs border border-slate-200">
          {match[12]}
        </code>
      );
    } else if (match[13]) {
      // Raw URL: https://...
      const rawUrl = match[13];
      elements.push(renderAnchorLink(rawUrl, rawUrl, `rawurl-${keyCounter++}`, options));
    }

    lastIndex = tokenRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    const trailingText = text.substring(lastIndex);
    elements.push(...enrichPlainTextWithAutoLinks(trailingText, keyCounter++, options, usedRules));
  }

  return elements;
}

/**
 * Enriches plain text with automated high-authority contextual links (max 1 occurrence per matched rule)
 */
function enrichPlainTextWithAutoLinks(
  plainText: string,
  baseKey: number,
  options?: RenderOptions,
  usedRules: Set<string> = new Set()
): React.ReactNode[] {
  for (const rule of AUTO_LINK_RULES) {
    if (usedRules.has(rule.href)) continue;

    const matchIndex = plainText.search(rule.match);
    if (matchIndex !== -1) {
      const match = plainText.match(rule.match);
      if (match && match[0]) {
        usedRules.add(rule.href); // Mark rule as used so we don't repeat the same link
        const matchedText = match[0];
        const before = plainText.substring(0, matchIndex);
        const after = plainText.substring(matchIndex + matchedText.length);

        return [
          before,
          renderAnchorLink(rule.href, matchedText, `autolink-${baseKey}`, options, rule.title),
          ...enrichPlainTextWithAutoLinks(after, baseKey + 100, options, usedRules)
        ];
      }
    }
  }

  return [plainText];
}

/**
 * Renders an anchor link with proper external/internal security attributes
 */
export function renderAnchorLink(
  href: string,
  label: React.ReactNode,
  key: string,
  options?: RenderOptions,
  title?: string
): React.ReactNode {
  const isExternal = /^https?:\/\//i.test(href) && !href.includes('harrydesk.com') && !href.startsWith('/');
  const isInternalHarryDesk = href.includes('harrydesk.com') || href.startsWith('/');

  let cleanHref = href;
  if (isInternalHarryDesk && href.includes('harrydesk.com')) {
    try {
      const urlObj = new URL(href);
      cleanHref = urlObj.pathname + urlObj.search;
    } catch {
      cleanHref = href.replace(/^https?:\/\/harrydesk\.com/i, '') || '/';
    }
  }

  if (isExternal) {
    const isCommercial = /payoneer\.com/i.test(href);
    const relAttr = isCommercial ? "nofollow noopener noreferrer" : "noopener noreferrer";

    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel={relAttr}
        title={title || 'External authority resource (opens in new window)'}
        className="text-amber-700 hover:text-amber-900 underline font-semibold transition inline-flex items-center gap-0.5 cursor-pointer"
        onClick={() => {
          if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
            (window as any).gtag('event', 'click', {
              event_category: 'outbound_seo_link',
              event_label: href,
              link_url: href,
              transport_type: 'beacon'
            });
          }
        }}
      >
        <span>{label}</span>
        <ExternalLink className="w-3 h-3 inline-block ml-0.5 opacity-70 flex-shrink-0" />
      </a>
    );
  }

  return (
    <a
      key={key}
      href={cleanHref}
      title={title || 'HarryDesk platform portal'}
      onClick={(e) => {
        if (options?.onInternalLinkClick && cleanHref.startsWith('/')) {
          e.preventDefault();
          options.onInternalLinkClick(cleanHref);
        }
      }}
      className="text-amber-700 hover:text-amber-900 underline font-semibold transition cursor-pointer"
    >
      {label}
    </a>
  );
}

/**
 * Full Markdown Document Renderer for Blog Post Content
 */
export function renderBlogMarkdown(content: string, options?: RenderOptions): React.ReactNode {
  if (!content) return null;

  const rawBlocks = content.split(/\n\n+/);
  const renderedElements: React.ReactNode[] = [];
  const usedRules = new Set<string>();

  rawBlocks.forEach((block, blockIndex) => {
    const trimmed = block.trim();
    if (!trimmed) return;

    // 1. Horizontal Rule
    if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
      renderedElements.push(
        <hr key={`hr-${blockIndex}`} className="my-8 border-t border-slate-200" />
      );
      return;
    }

    // 2. Headings (Semantic H1, H2, H3, H4)
    if (trimmed.startsWith('#### ')) {
      renderedElements.push(
        <h4 key={`h4-${blockIndex}`} className="text-xs md:text-sm font-bold text-amber-700 tracking-tight mt-6 mb-2 font-mono uppercase">
          {parseInlineMarkdown(trimmed.replace(/^####\s+/, ''), options, usedRules)}
        </h4>
      );
      return;
    }
    if (trimmed.startsWith('### ')) {
      renderedElements.push(
        <h3 key={`h3-${blockIndex}`} className="text-base md:text-xl font-black text-slate-900 tracking-tight mt-8 mb-3 font-display">
          {parseInlineMarkdown(trimmed.replace(/^###\s+/, ''), options, usedRules)}
        </h3>
      );
      return;
    }
    if (trimmed.startsWith('## ')) {
      renderedElements.push(
        <h2 key={`h2-${blockIndex}`} className="text-xl md:text-2xl font-black text-slate-900 tracking-tight mt-10 mb-4 font-display">
          {parseInlineMarkdown(trimmed.replace(/^##\s+/, ''), options, usedRules)}
        </h2>
      );
      return;
    }
    if (trimmed.startsWith('# ')) {
      renderedElements.push(
        <h1 key={`h1-${blockIndex}`} className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight mt-10 mb-4 font-display">
          {parseInlineMarkdown(trimmed.replace(/^#\s+/, ''), options, usedRules)}
        </h1>
      );
      return;
    }

    // 3. Blockquote
    if (trimmed.startsWith('> ')) {
      const quoteLines = trimmed.split('\n').map(l => l.replace(/^>\s*/, '')).join(' ');
      renderedElements.push(
        <blockquote key={`quote-${blockIndex}`} className="my-5 border-l-4 border-amber-500 bg-amber-50/50 p-4 md:p-5 rounded-r-2xl text-slate-700 italic text-sm md:text-base leading-relaxed">
          {parseInlineMarkdown(quoteLines, options, usedRules)}
        </blockquote>
      );
      return;
    }

    // 4. Code Block
    if (trimmed.startsWith('```')) {
      const lines = trimmed.split('\n');
      const lastLine = lines && lines.length > 0 ? lines[lines.length - 1] : '';
      const codeContent = (lines && lines.length > 1)
        ? lines.slice(1, (lastLine && lastLine.startsWith('```')) ? -1 : undefined).join('\n')
        : '';
      renderedElements.push(
        <pre key={`pre-${blockIndex}`} className="my-5 p-4 md:p-5 rounded-2xl bg-slate-900 text-slate-100 font-mono text-xs overflow-x-auto leading-relaxed border border-slate-800 shadow-xs">
          <code>{codeContent}</code>
        </pre>
      );
      return;
    }

    // 5. Markdown Table
    if (trimmed.startsWith('|') && trimmed.includes('\n|')) {
      const rows = trimmed.split('\n').filter(r => r.trim().startsWith('|'));
      if (rows && rows.length >= 2 && rows[0]) {
        const headerCells = (rows[0] ? rows[0].split('|') : []).slice(1, -1).map(c => c.trim());
        const dataRows = rows.slice(2); // skip delimiter row

        renderedElements.push(
          <div key={`table-${blockIndex}`} className="my-6 overflow-x-auto rounded-2xl border border-slate-200 shadow-xs bg-white">
            <table className="min-w-full divide-y divide-slate-200 text-left text-xs font-sans">
              <thead className="bg-slate-50">
                <tr>
                  {headerCells.map((header, hIdx) => (
                    <th key={hIdx} className="px-4 py-3 font-bold text-slate-900 uppercase tracking-wider font-mono text-[11px]">
                      {parseInlineMarkdown(header, options, usedRules)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {dataRows.map((rowStr, rIdx) => {
                  const cells = (rowStr ? rowStr.split('|') : []).slice(1, -1).map(c => c.trim());
                  return (
                    <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      {cells.map((cell, cIdx) => (
                        <td key={cIdx} className="px-4 py-3 text-slate-700 leading-normal">
                          {parseInlineMarkdown(cell, options, usedRules)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
        return;
      }
    }

    // 6. Unordered List
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const listItems = trimmed.split('\n').filter(l => l.trim().startsWith('- ') || l.trim().startsWith('* '));
      renderedElements.push(
        <ul key={`ul-${blockIndex}`} className="list-disc pl-5 my-3 space-y-2 text-slate-700 text-sm md:text-base leading-relaxed">
          {listItems.map((li, liIdx) => (
            <li key={liIdx} className="pl-1">
              {parseInlineMarkdown(li.replace(/^[\s\-\*]+\s*/, ''), options, usedRules)}
            </li>
          ))}
        </ul>
      );
      return;
    }

    // 7. Ordered List
    if (/^\d+\.\s/.test(trimmed)) {
      const listItems = trimmed.split('\n').filter(l => /^\d+\.\s/.test(l.trim()));
      renderedElements.push(
        <ol key={`ol-${blockIndex}`} className="list-decimal pl-5 my-3 space-y-2 text-slate-700 text-sm md:text-base leading-relaxed">
          {listItems.map((li, liIdx) => (
            <li key={liIdx} className="pl-1">
              {parseInlineMarkdown(li.replace(/^\d+\.\s+/, ''), options, usedRules)}
            </li>
          ))}
        </ol>
      );
      return;
    }

    // 8. Standard Paragraph
    renderedElements.push(
      <p key={`p-${blockIndex}`} className="text-sm md:text-base text-slate-700 leading-relaxed mb-4">
        {parseInlineMarkdown(trimmed, options, usedRules)}
      </p>
    );
  });

  return <>{renderedElements}</>;
}
